// useMarketplaceVisible — which employee-facing financial surfaces to show.
// `visible` (My Products): true only when the employer's marketplace toggle is on AND
// the tenant has ≥1 product enabled for this employee (or they have a loan/application
// in flight). `loansVisible` (Loans & Advances): the employer's loans toggle.
// Both are computed server-side by credit-service /me/products-visible from
// credit.tenant_settings — the employer's key of the two-key gate.
//
// UX only — the backend already filters disabled products out of every /me endpoint.
// Session-cached like useEntitlements. `enabled` gates the fetch so tenants without the
// credit module never call /credit/*.
import { useEffect, useState } from 'react';
import { creditApi } from '@/features/credit/api/creditApi';

interface Flags {
  visible: boolean;
  loansVisible: boolean;
}

// On failure: hide My Products (empty area is worse than absent), but keep Loans
// visible (fail open — an outage must not strip an existing surface from employees).
const FALLBACK: Flags = { visible: false, loansVisible: true };

let cache: Flags | null = null; // module-level (per session)

export function useMarketplaceVisible(enabled: boolean): { loaded: boolean } & Flags {
  const [flags, setFlags] = useState<Flags>(cache ?? FALLBACK);
  const [loaded, setLoaded] = useState<boolean>(cache !== null || !enabled);

  useEffect(() => {
    if (!enabled) return;
    if (cache !== null) {
      setFlags(cache);
      setLoaded(true);
      return;
    }
    let alive = true;
    creditApi
      .productsVisible()
      .then((v) => {
        cache = v;
        if (alive) {
          setFlags(v);
          setLoaded(true);
        }
      })
      .catch(() => {
        cache = FALLBACK;
        if (alive) {
          setFlags(FALLBACK);
          setLoaded(true);
        }
      });
    return () => {
      alive = false;
    };
  }, [enabled]);

  return { loaded, ...flags };
}

/** Reset the session cache (call after an admin changes product/visibility settings). */
export function resetMarketplaceVisible(): void {
  cache = null;
}
